// === Add-to-Home-Screen (install) — persistent, phone/tablet only ===
//
// The install entry is ALWAYS available on a phone/tablet (the Home overview
// panel + the gear/Settings button) — never on a mouse desktop, never when
// already installed, and with no auto popup nagging on first open.
//
// Tapping it does the best the current browser allows:
//   - Chromium (Chrome/Edge/Opera/Samsung): the captured native one-tap prompt
//   - everything else (Firefox, Safari, …): a short, phone-specific "add to
//     home screen" instruction tailored to that exact browser.

import { showToast } from '../utils/toast.js';

let deferredPrompt = null;
let installed = false;

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

// Mouse-driven desktop (not a touch phone/tablet). The PWA is a phone companion,
// so we keep install affordances off the computer.
function isDesktop() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

// Show the install entry on any phone/tablet that isn't already the installed
// app. Deliberately NOT gated on the native prompt — otherwise the button would
// vanish whenever the browser isn't currently offering it.
export function canInstall() {
  // Install affordances intentionally disabled ahead of the app-store release —
  // a web "add to home screen" entry only confuses users before the native
  // apps ship. Re-enable by restoring the checks below.
  return false;
  // return !installed && !isStandalone() && !isDesktop();
}

// Short, phone-specific "add to home screen" steps for the current browser.
function installInstructions() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
      ? 'On iPhone this only works in Safari — open this page in Safari, then tap Share → “Add to Home Screen”.'
      : 'Tap the Share icon at the bottom of Safari, then choose “Add to Home Screen”.';
  }
  if (/Firefox/i.test(ua)) return 'Tap the menu (⋮), then choose “Add to Home screen”.';
  if (/SamsungBrowser/i.test(ua)) return 'Tap the menu, choose “Add page to”, then “Home screen”.';
  if (/OPR|OPT|Opera/i.test(ua)) return 'Tap the Opera menu, then choose “Add to home screen”.';
  return 'Tap the browser menu (⋮), then choose “Install app” or “Add to Home screen”.';
}

// Capture the install prompt as EARLY as possible — these listeners run at
// module load (during import, before the app's async init()), so we never miss
// Chrome's beforeinstallprompt (it can fire before init() finishes loading data).
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
});
window.addEventListener('appinstalled', () => {
  installed = true;
  deferredPrompt = null;
  syncSettingsButton();
  syncHomeInstall();
  const banner = document.querySelector('.install-hint');
  if (banner) banner.remove();
  showToast('✓ Added to your home screen', 5000);
});

// Initial sync of the install affordances once the DOM is ready.
export function setupPwaInstall() {
  syncSettingsButton();
  syncHomeInstall();
}

// Trigger the install flow. Native one-tap prompt when the browser offers it;
// otherwise a phone-specific "add to home screen" instruction for this browser.
export async function triggerInstall() {
  if (deferredPrompt) {
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch { /* ignore */ }
    deferredPrompt = null;
    return true;
  }
  showBanner(`
    ${LOGO}
    <span class="install-hint-text">${installInstructions()}</span>
    <button type="button" class="install-hint-dismiss" aria-label="Dismiss">&times;</button>
  `);
  return true;
}

const LOGO = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16"/></svg>`;

function showBanner(innerHtml) {
  const existing = document.querySelector('.install-hint');
  if (existing) existing.remove();
  const el = document.createElement('div');
  el.className = 'install-hint';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Add to home screen');
  el.innerHTML = innerHtml;
  el.querySelector('.install-hint-dismiss').addEventListener('click', () => el.remove());
  document.body.appendChild(el);
  return el;
}

// Show or hide the install button inside the Settings (config) card.
function syncSettingsButton() {
  const btn = document.getElementById('btnInstallApp');
  if (!btn) return;
  btn.hidden = !canInstall();
}

// Show or hide the install panel on the Home overview.
function syncHomeInstall() {
  const panel = document.getElementById('homeInstall');
  if (panel) panel.hidden = !canInstall();
}
